import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, X, ChevronDown, Star, Shield, Eye, Moon, Sparkles, Heart, Leaf } from 'lucide-react';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const FiveReasonsEyeMask = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Add variant=b to URL for Clarity filtering if not already present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.has('variant')) {
      searchParams.set('variant', 'b');
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }
  }, []);
  
  // Get current URL query parameters and append them to destination URLs
  // Includes variant=b for Clarity filtering if not already present
  const getUrlWithParams = (baseUrl) => {
    const searchParams = new URLSearchParams(location.search);
    
    // Only add variant=b if no variant param exists yet
    if (!searchParams.has('variant')) {
      searchParams.set('variant', 'b');
    }
    
    const paramsString = searchParams.toString();
    return paramsString ? `${baseUrl}?${paramsString}` : baseUrl;
  };

  const productUrl = useMemo(() => 
    getUrlWithParams("https://kickinon.com/products/self-heating-steam-eye-mask"),
    [location.search]
  );

  const faqData = [
    {
      question: "How long does each Kickin'On Steam Eye Mask last?",
      answer: "Each Kickin'On mask provides a consistent 20–25 minutes of gentle, warming steam. It's a great window to unwind, whether you're in bed, on the couch, or trying to relax on a flight."
    },
    {
      question: "Can I use it every night?",
      answer: "Yes. Kickin'On is designed to be a cosy nightly ritual. Many people use one most evenings and keep a spare pack in their travel bag for flights and hotel nights."
    },
    {
      question: "What does it feel like when it's on?",
      answer: "Most people describe it as a soft, lightly weighted hug for their eyes. The mask warms up on its own, the world goes dark, and your face finally gets a moment to relax."
    },
    {
      question: "Is it travel friendly?",
      answer: "Absolutely. Each mask is single-use, cordless and compact, making it perfect for carry-ons, overnight bags and long-haul flights. No liquids, no batteries, no fuss."
    },
    {
      question: "Do I need to change anything else in my routine?",
      answer: "No. You don't have to overhaul your life to enjoy it. Just add Kickin'On as a small, warm cue at the end of your day — or at the start of a long flight — and let the warmth and darkness do the rest."
    }
  ];

  const reviews = [
    {
      author: "Sarah M.",
      text: "I used to lie in bed scrolling because I couldn't switch off. Now I put this on, feel my eyes relax, and I drift off so much easier. My mornings feel a lot less brutal."
    },
    {
      author: "Jessica L.",
      text: "It's like a tiny spa moment for my face. The warmth, the darkness, the quiet… I finally feel my whole body unclench. I wake up feeling way more rested than I expect based on my sleep."
    },
    {
      author: "Amanda K.",
      text: "This is my 'day is officially over' moment. Ten to fifteen minutes with the mask on and my brain finally gets the memo to slow down."
    },
    {
      author: "Michelle R.",
      text: "A lifesaver for flights. I use one after takeoff and actually arrive feeling semi-human instead of wrecked. It lives in my carry-on now."
    },
    {
      author: "Lauren T.",
      text: "Finally a ritual I've stuck with. I use Kickin'On most nights and before big days. My evenings feel calmer and my mornings less chaotic."
    },
    {
      author: "Rachel W.",
      text: "I keep a box in my nightstand and another in my work bag. On huge days I'll use one in the afternoon and one at night. It's my reset button."
    }
  ];

  const timelineData = [
    {
      period: "Week 1–2: Your new wind-down cue",
      description: "You start linking the warm steam and darkness with \"time to slow down.\" Evenings and travel days begin to feel a little more contained."
    },
    {
      period: "Week 3–4: Evenings feel more intentional",
      description: "Using your mask becomes a familiar signal that the busy part of the day is over. Nights feel a bit less chaotic and more like a soft landing."
    },
    {
      period: "Month 2: Calm becomes part of the routine",
      description: "Your evening and travel steam ritual starts to feel automatic. You know exactly how you'll close out the day, wherever you are."
    },
    {
      period: "Month 3: A ritual you protect like your coffee",
      description: "You look forward to your 10–15 minutes of warm darkness. Bedtime stops feeling like a battle and becomes a moment you actually protect."
    }
  ];

  const comparisonFeatures = [
    "Self-heating, gentle steam for a cosy wind-down",
    "Creates a simple \"end of day\" cue you can repeat anywhere",
    "Single-use, hygienic & travel-ready",
    "Even warmth for 20+ minutes — no cords, no microwave",
    "Designed as a nightly & travel wind-down ritual"
  ];

  const benefits = [
    "Gentle steam for a quick, cosy wind-down",
    "Feels soft and comforting around the eyes",
    "Supports a calmer evening wind-down routine",
    "Fresh, single-use & hygienic",
    "Cord-free, travel-friendly & ready anywhere"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Helmet>
        <title>
          The Tiny Japanese-Inspired Ritual Helping You Switch Off — Even 36,000 Feet in the Air
        </title>
        <meta
          name="description"
          content="Discover the cosy, self-heating steam eye mask ritual inspired by Japan that helps people wind down at night, on flights, and in hotel rooms — and feel a little more at ease."
        />
        <meta
          name="keywords"
          content="steam eye mask, wind-down ritual, travel comfort, Japanese eye mask, bedtime routine, relax before sleep, flight comfort"
        />
        <meta
          property="og:title"
          content="The Tiny Japanese-Inspired Ritual Helping You Switch Off — Even 36,000 Feet in the Air"
        />
        <meta
          property="og:description"
          content="A cosy self-heating steam mask that turns flights, hotel rooms, and busy nights into a small pocket of calm at the end of the day."
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="The Tiny Japanese-Inspired Ritual Helping You Switch Off — Even 36,000 Feet in the Air"
        />
        <meta
          name="twitter:description"
          content="See why people are adding this Japanese-inspired steam ritual to their travel bags and nightstands."
        />
        <link
          rel="canonical"
          href="https://kickinon.com/pages/switch-off-ritual"
        />
      </Helmet>

      {/* Sticky Top Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-green-800 to-green-700 shadow-lg">
        <div className="flex items-center justify-center h-14 px-4">
          <Badge variant="secondary" className="bg-green-600/20 text-white border-green-500/30 text-sm font-semibold">
            ⭐ Trending Nighttime Spa Treatment To Wake Up Refreshed
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto pt-20 px-4 sm:px-6 lg:px-8">
        
        {/* Hero Section */}
        <section className="text-center py-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-green-900 leading-tight mb-6">
            5 Reasons People Switch to a Steam Eye Mask Before Bed
          </h1>
          
          <p className="text-xl text-green-700 font-medium max-w-3xl mx-auto mb-4">
            A self-heating steam treatment that gives your eyes a spa-level reset — without spa prices
          </p>
          
          <p className="text-lg text-green-600/80 max-w-3xl mx-auto mb-8">
            For screen-tired eyes, restless nights, and people who want to wake up feeling human again.
          </p>
          
          <div className="flex items-center justify-center gap-4 text-sm text-green-600 mb-6">
            <span className="font-semibold">By Jennifer K.</span>
            <Separator orientation="vertical" className="h-4 bg-green-300" />
            <span>Last Updated December 2025</span>
          </div>
          
          <Card className="border-2 border-dashed border-amber-400 bg-amber-50 max-w-2xl mx-auto">
            <CardContent className="py-4">
              <p className="text-amber-700">
                <span className="font-bold text-amber-800">UPDATE:</span> Selling out faster than expected — Take it anywhere open the packet for an instant warm compress.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Reason 1 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="aspect-square md:aspect-auto">
              <img
                src="/images/reason6.jpg"
                alt="Spa-style warm compress steam eye mask"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                1. It delivers a real spa-style warm compress — automatically
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  Spas use warm compresses for a reason: steady heat helps eyes relax.
                </p>
                <p>
                  Kickin'On uses a self-heating steam layer that warms evenly on its own, releasing gentle moisture around the eyes — no microwave, no guessing, no hot spots.
                </p>
                <p>
                  <strong className="text-green-800">Same concept. Way easier. No appointment required.</strong>
                </p>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Reason 2 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white order-2 md:order-1">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                2. Steam feels better on tired, dry eyes than cold gels
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  Cold masks can feel nice at first, but they don't help eyes fully let go at night.
                </p>
                <p>
                  Warm steam surrounds the eye area with soft heat and moisture, helping screen-tired, dry-feeling eyes finally relax before sleep — instead of just numbing them.
                </p>
              </div>
            </CardContent>
            <div className="aspect-square md:aspect-auto order-1 md:order-2">
              <img
                src="/images/reason4.jpg"
                alt="Steam vs cold gel eye masks"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </Card>

        {/* Reason 3 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="aspect-square md:aspect-auto">
              <img
                src="/images/reason1.jpg"
                alt="Warmth and darkness helps brain switch off"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                3. Warmth + darkness helps your brain switch off
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  This isn't just about eye comfort.
                </p>
                <p>
                  When warmth and darkness cover the eyes, your nervous system gets a clear cue: <strong className="text-green-800">the day is done.</strong>
                </p>
                <p>
                  For many people, that 10–15 minute steam window becomes the moment scrolling stops and rest begins.
                </p>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* CTA after Reason 3 with Testimonial */}
        <Card className="mb-8 bg-gradient-to-r from-green-700 to-green-600 border-0 shadow-2xl overflow-hidden">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="flex justify-center">
                <img
                  src="/images/offer-section.jpg"
                  alt="Kickin'On Steam Eye Mask - Buy 2 Get 1 Free"
                  className="w-full max-w-sm rounded-xl shadow-xl"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="text-center md:text-left">
                {/* Testimonial */}
                <Card className="bg-white/10 border-white/20 mb-6">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex text-amber-300">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                      <span className="text-white/90 font-semibold text-sm">Jessica L.</span>
                    </div>
                    <p className="text-white/90 text-sm italic leading-relaxed">
                      "It's like a tiny spa moment for my face. The warmth, the darkness, the quiet… I finally feel my whole body unclench. I wake up feeling way more rested than I expect based on my sleep."
                    </p>
                    <Badge variant="outline" className="text-white/80 border-white/30 text-xs mt-3">
                      ✓ Verified Purchase
                    </Badge>
                  </CardContent>
                </Card>
                
                <h3 className="text-2xl font-extrabold text-white mb-2">
                  👉 Ready to Wake Up Refreshed?
                </h3>
                <p className="text-white/90 text-lg font-semibold mb-4">
                  Start Your Wind-Down Ritual Tonight
                </p>
                <Badge className="bg-white/20 text-white border-white/30 text-sm font-bold mb-6">
                  Buy 2 Get 1 FREE — Limited Time Only
                </Badge>
                <div>
                  <Button
                    asChild
                    size="lg"
                    className="bg-white text-green-700 hover:bg-green-50 font-bold shadow-lg hover:shadow-xl transition-all w-full md:w-auto"
                  >
                    <a
                      href={productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      GET YOUR STEAM MASK BUNDLE
                    </a>
                  </Button>
                </div>
                <p className="text-white font-medium text-sm mt-4">
                  30-Day Risk-Free Trial
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reason 4 */}
        <Card className="mb-8 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white order-2 md:order-1">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                4. It's a simple ritual you can actually stick to
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  No routines to track. No tech. No prep.
                </p>
                <p>
                  Open the packet. Place the mask. Close your eyes.<br />
                  Let the warmth rise for 10–15 minutes.
                </p>
                <p>
                  Even on late nights, messy nights, or travel days — it still works.
                </p>
              </div>
            </CardContent>
            <div className="aspect-square md:aspect-auto order-1 md:order-2">
              <img
                src="/images/reason7.jpg"
                alt="Simple bedtime ritual with steam eye mask"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </Card>

        {/* Reason 5 */}
        <Card className="mb-12 border-green-200 shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="aspect-square md:aspect-auto">
              <img
                src="/images/WO_lady_KickinOn-Steam-Eye-Mask---Dry-Eyes-and-Eye-Strain-Relief-10.jpg"
                alt="Wake up feeling refreshed after using steam eye mask"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <CardContent className="p-6 lg:p-8 flex flex-col justify-center bg-white">
              <h2 className="text-2xl font-bold text-green-900 mb-4">
                5. It gives nights a softer ending — and mornings a gentler start
              </h2>
              <div className="space-y-4 text-green-700/90">
                <p>
                  How you wind down matters more than most people realise.
                </p>
                <p>
                  By giving your eyes and brain a short steam reset before bed, many people say they fall asleep feeling calmer — and wake up feeling less wrecked.
                </p>
                <p>
                  <strong className="text-green-800">Not perfect. Just better.</strong>
                </p>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Reviews Section */}
        <Card className="mb-12 border-green-200 shadow-xl bg-white">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-3xl font-extrabold text-green-900">
              What people are saying
            </CardTitle>
            <CardDescription className="text-green-600 text-base mt-2">
             Reviews from people who added Kickin'On to
              their nights & carry-ons
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.map((review, index) => (
                <Card key={index} className="border-l-4 border-l-green-500 bg-green-50/50 border-green-100">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-green-900">{review.author}</span>
                      <div className="flex text-amber-400">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-300 text-xs mb-3">
                      ✓ Verified Purchase
                    </Badge>
                    <p className="text-sm text-green-700/80 leading-relaxed">
                      "{review.text}"
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Timeline Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-green-900 mb-4">
            Your Kickin'On Evening & Travel Ritual Timeline
          </h2>
          <p className="text-green-600 mb-8">
            Most people feel a shift in how they wind down once they turn this into a tiny nightly (and travel day) habit 👇
          </p>

          <div className="space-y-4">
            {timelineData.map((item, index) => (
              <Card key={index} className="border-green-200 bg-green-50 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-green-600"></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-green-900 mb-2">{item.period}</h3>
                    <p className="text-green-700/80">{item.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button
              asChild
              size="lg"
              className="w-full max-w-xl bg-gradient-to-r from-green-700 to-green-600 hover:from-green-800 hover:to-green-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <a href={productUrl}>
                START YOUR EVENING & TRAVEL RITUAL
              </a>
            </Button>
            <p className="mt-4 text-green-600">
              Tiny mask. Big reset for tired eyes & busy minds.
            </p>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-green-900 mb-4">
            How Kickin'On Steam Eye Mask Stands Apart
          </h2>
          <p className="text-green-600 mb-8">
            Most "eye care" products only cool or coat. Kickin'On uses{' '}
            <strong className="text-green-800">gentle, warming steam</strong> to create a cosy wind-down moment — at home or on the move 👇
          </p>

          <Card className="border-2 border-green-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-green-50 hover:bg-green-50">
                  <TableHead className="w-[100px] text-center">
                    <div className="bg-green-100 rounded-lg p-2 inline-block">
                      <img
                        src="/images/offer-section.jpg"
                        alt="Kickin'On Icon"
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </TableHead>
                  <TableHead className="text-green-900 font-semibold">Feature</TableHead>
                  <TableHead className="text-center text-green-900 font-semibold">Basic Cooling Masks & Gels</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonFeatures.map((feature, index) => (
                  <TableRow key={index} className="hover:bg-green-50/50">
                    <TableCell className="text-center bg-green-50">
                      <div className="inline-flex items-center justify-center w-7 h-7 rounded bg-green-500">
                        <Check className="w-5 h-5 text-white" strokeWidth={3} />
                      </div>
                    </TableCell>
                    <TableCell className="text-green-800 font-medium">{feature}</TableCell>
                    <TableCell className="text-center">
                      <X className="w-7 h-7 text-red-500 mx-auto" strokeWidth={3} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>

        {/* Final Message */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-green-900 mb-6">
            Reclaim calmer nights & easier travel with{' '}
            <span className="text-green-600">Kickin'On</span>
          </h2>

          <div className="space-y-4 text-green-700/90 mb-8">
            <p>
              If your days are full, your brain is loud, and your nights feel
              more chaotic than calm — you're not alone. Most of us were never
              taught how to actually switch off.
            </p>
            <p>
              <strong className="text-green-800">Kickin'On Steam Eye Mask</strong> gives you a tiny,
              Japanese-inspired ritual: warm steam, soft pressure, and darkness that gently mark the end of the day.
            </p>
            <p>
              No complicated routine. No apps. No pressure to "fix" yourself.
              Just one small, cosy habit you can take to bed, on flights, into
              hotel rooms, and through the seasons of life where you're running
              on fumes but still showing up.
            </p>
            <p>
              One mask at night can become your simple signal to unwind, slow down, and feel a little more like the version of you that isn't constantly running behind.
            </p>
          </div>

          {/* Photo Grid */}
          <div className="grid grid-cols-3 gap-3 max-w-2xl mx-auto mb-8">
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Eye className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Moon className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Leaf className="w-12 h-12 text-green-400" />
            </div>
            <a href={productUrl} target="_blank" rel="noopener noreferrer" className="block">
              <Card className="aspect-square rounded-xl bg-gradient-to-br from-green-600 to-green-700 border-0 flex items-center justify-center hover:from-green-700 hover:to-green-800 transition-all cursor-pointer hover:scale-105">
                <CardContent className="p-4 text-center text-white">
                  <div className="text-xs mb-1">★ ★ ★ ★ ★</div>
                  <div className="text-[10px] font-bold tracking-wide">RISK-FREE</div>
                  <div className="text-2xl font-extrabold">30-DAY</div>
                  <div className="text-[10px] font-semibold">TRY IT OUT</div>
                  <div className="text-[10px] mt-1 opacity-90">rated 4.7/5</div>
                </CardContent>
              </Card>
            </a>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Heart className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Shield className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Star className="w-12 h-12 text-green-400" />
            </div>
            <div className="aspect-square rounded-xl bg-green-100 flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-green-400" />
            </div>
          </div>

          {/* Benefits List */}
          <ul className="space-y-3 mb-8">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-green-800 font-medium">
                <div className="flex-shrink-0 w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                {benefit}
              </li>
            ))}
          </ul>

          <p className="text-lg font-semibold text-green-800 text-center mb-6">
            Your calm matters. Your rest matters. And your eyes deserve a soft
            landing at the end of every day — and every flight.
          </p>

          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-green-700 to-green-600 hover:from-green-800 hover:to-green-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <a href={productUrl}>
                TRY KICKIN'ON STEAM EYE MASK RISK-FREE
              </a>
            </Button>
            <p className="mt-4 text-green-600">
              Still here. Still tired. Still Kickin'On.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <Card className="mb-12 bg-gradient-to-r from-green-700 to-green-600 border-0 shadow-2xl overflow-hidden">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="flex justify-center">
                <img
                  src="/images/offer-section.jpg"
                  alt="Kickin'On Steam Eye Mask - Buy 2 Get 1 Free"
                  className="w-full max-w-sm rounded-xl shadow-xl"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-extrabold text-white mb-2">
                  👉 Start Your Evening & Travel Ritual
                </h3>
                <p className="text-white/90 text-lg font-semibold mb-4">
                  Kickin'On Steam Eye Mask — The Japanese-Inspired Wind-Down
                  Ritual
                </p>
                <Badge className="bg-white/20 text-white border-white/30 text-sm font-bold mb-6">
                  Buy 2 Get 1 FREE — Limited Time Only
                </Badge>
                <div>
                  <Button
                    asChild
                    size="lg"
                    className="bg-white text-green-700 hover:bg-green-50 font-bold shadow-lg hover:shadow-xl transition-all w-full md:w-auto"
                  >
                    <a
                      href={productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      GET YOUR STEAM MASK BUNDLE
                    </a>
                  </Button>
                </div>
                <p className="text-white/80 text-sm mt-4">
                  Tiny mask. Big reset for nights, flights & everything in
                  between.
                </p>
                <p className="text-white font-medium text-sm mt-2">
                  30-Day Risk-Free Trial
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-green-900 text-center mb-8">
            Frequently Asked Questions
          </h2>

          <Accordion type="single" collapsible className="space-y-3">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-2 border-green-200 rounded-xl overflow-hidden bg-white hover:border-green-400 transition-colors"
              >
                <AccordionTrigger className="px-6 py-4 text-left text-green-900 font-semibold hover:text-green-700 hover:no-underline [&[data-state=open]>svg]:text-green-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-green-700/80">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default FiveReasonsEyeMask;
